import React, { useCallback, useEffect, useRef, useState } from "react";
import type { HfPlayerRef } from "../../hyperframesPlayer";
import { Tooltip } from "./Tooltip";
import { Popover, MenuSection, MenuItem } from "./ui/Primitives";

export type TransportBarProps = {
  player: HfPlayerRef | null;
  fps: number;
  currentFrame: number;
  totalFrames: number;
  isPlaying: boolean;
  speed: number;
  locked: boolean;
  loop: boolean;
  snap: boolean;
  muted: boolean;
  onSpeedChange: (next: number) => void;
  onLockToggle: () => void;
  onLoopToggle: () => void;
  onSnapToggle: () => void;
  onMuteToggle: () => void;
  /** When provided, shortcut listeners are scoped to this element. Defaults to window. */
  hotkeyScope?: React.RefObject<HTMLElement | null>;
};

const SPEEDS = [0.25, 0.5, 1, 1.5, 2] as const;

/**
 * Global transport bar — shared player controls (play/pause, frame-step,
 * jump start/end), speed selector, loop, lock (disables direct-manipulation
 * editing in the preview), grid-snap toggle, mute, and live time/frame
 * readout. Also wires keyboard shortcuts.
 *
 * Shortcuts:
 *   Space          play / pause
 *   ←  / →         step ∓1 frame  (Shift = ∓10 frames)
 *   Home / End     jump to start / end
 *   L              toggle lock
 *   G              toggle snap-to-grid
 *   M              toggle mute
 *   O              toggle loop
 *   F              toggle fullscreen
 *   1 / 2 / 3      speed 0.5× / 1× / 2×
 */
export const TransportBar: React.FC<TransportBarProps> = ({
  player,
  fps,
  currentFrame,
  totalFrames,
  isPlaying,
  speed,
  locked,
  loop,
  snap,
  muted,
  onSpeedChange,
  onLockToggle,
  onLoopToggle,
  onSnapToggle,
  onMuteToggle,
  hotkeyScope,
}) => {
  // Refs so the keyboard listener (registered ONCE) always sees the latest values
  // without re-binding on every state tick.
  const stateRef = useRef({ player, isPlaying, totalFrames, currentFrame });
  stateRef.current = { player, isPlaying, totalFrames, currentFrame };

  const seekTo = useCallback((frame: number): void => {
    const p = stateRef.current.player;
    if (!p) return;
    const clamped = Math.max(0, Math.min(stateRef.current.totalFrames - 1, Math.round(frame)));
    p.seekTo(clamped);
  }, []);

  const togglePlay = useCallback((): void => {
    const p = stateRef.current.player;
    if (!p) return;
    if (stateRef.current.isPlaying) p.pause();
    else p.play();
  }, []);

  const stepFrame = useCallback(
    (delta: number): void => {
      const p = stateRef.current.player;
      if (!p) return;
      if (stateRef.current.isPlaying) p.pause();
      seekTo(stateRef.current.currentFrame + delta);
    },
    [seekTo],
  );

  const jumpStart = useCallback(() => seekTo(0), [seekTo]);
  const jumpEnd = useCallback(() => seekTo(stateRef.current.totalFrames - 1), [seekTo]);

  // ----------------------------------------------------------------
  // Fullscreen — uses the Player's built-in container request and
  // mirrors browser fullscreenchange events into local state so the
  // button icon/aria stay in sync (e.g. user presses Esc to exit).
  // ----------------------------------------------------------------
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);

  useEffect(() => {
    const sync = (): void => {
      const fsEl =
        document.fullscreenElement ??
        (document as Document & { webkitFullscreenElement?: Element | null }).webkitFullscreenElement ??
        null;
      setIsFullscreen(Boolean(fsEl));
    };
    document.addEventListener("fullscreenchange", sync);
    document.addEventListener("webkitfullscreenchange", sync);
    sync();
    return () => {
      document.removeEventListener("fullscreenchange", sync);
      document.removeEventListener("webkitfullscreenchange", sync);
    };
  }, []);

  const toggleFullscreen = useCallback((): void => {
    const p = stateRef.current.player;
    if (!p) return;
    try {
      if (p.isFullscreen()) p.exitFullscreen();
      else p.requestFullscreen();
    } catch {
      // requestFullscreen may throw if not in a user gesture or unsupported —
      // we silently swallow because the hotkey path can hit this safely.
    }
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent): void => {
      const target = e.target as HTMLElement | null;
      // Don't hijack when typing in inputs / contenteditables
      if (target) {
        const tag = target.tagName;
        if (
          tag === "INPUT" ||
          tag === "TEXTAREA" ||
          tag === "SELECT" ||
          target.isContentEditable
        ) {
          return;
        }
      }

      switch (e.key) {
        case " ":
        case "Spacebar":
          e.preventDefault();
          togglePlay();
          break;
        case "ArrowLeft":
          e.preventDefault();
          stepFrame(e.shiftKey ? -10 : -1);
          break;
        case "ArrowRight":
          e.preventDefault();
          stepFrame(e.shiftKey ? 10 : 1);
          break;
        case "Home":
          e.preventDefault();
          jumpStart();
          break;
        case "End":
          e.preventDefault();
          jumpEnd();
          break;
        case "l":
        case "L":
          e.preventDefault();
          onLockToggle();
          break;
        case "g":
        case "G":
          e.preventDefault();
          onSnapToggle();
          break;
        case "m":
        case "M":
          e.preventDefault();
          onMuteToggle();
          break;
        case "o":
        case "O":
          e.preventDefault();
          onLoopToggle();
          break;
        case "f":
        case "F":
          e.preventDefault();
          toggleFullscreen();
          break;
        case "1":
          e.preventDefault();
          onSpeedChange(0.5);
          break;
        case "2":
          e.preventDefault();
          onSpeedChange(1);
          break;
        case "3":
          e.preventDefault();
          onSpeedChange(2);
          break;
        default:
          break;
      }
    };

    const el = hotkeyScope?.current ?? window;
    el.addEventListener("keydown", handler as EventListener);
    return () => el.removeEventListener("keydown", handler as EventListener);
  }, [
    togglePlay,
    stepFrame,
    jumpStart,
    jumpEnd,
    onLockToggle,
    onSnapToggle,
    onMuteToggle,
    onLoopToggle,
    onSpeedChange,
    toggleFullscreen,
    hotkeyScope,
  ]);

  const totalSec = totalFrames / fps;
  const curSec = currentFrame / fps;
  const progressPct = totalFrames <= 0 ? 0 : Math.min(100, (currentFrame / Math.max(1, totalFrames - 1)) * 100);

  const onScrub = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const v = Number(e.target.value);
    if (!Number.isFinite(v)) return;
    if (isPlaying) player?.pause();
    seekTo(v);
  };

  return (
    <div className={`transport-bar ${locked ? "locked" : ""}`} role="toolbar" aria-label="Player Transport">
      <div className="transport-row main">
        <div className="transport-cluster">
          <Tooltip title="Zum Anfang springen" description="Setzt den Playhead auf Frame 0." shortcut="Home">
            <button className="t-btn" onClick={jumpStart} aria-label="Zum Anfang" data-tip-host="true">
              ⏮
            </button>
          </Tooltip>
          <Tooltip title="10 Frames zurück" description="Springt 10 Frames (≈ 0,33s bei 30fps) rückwärts." shortcut="⇧ ←">
            <button
              className="t-btn"
              onClick={() => stepFrame(-10)}
              aria-label="10 Frames zurück"
              data-tip-host="true"
            >
              ⏪
            </button>
          </Tooltip>
          <Tooltip title="Ein Frame zurück" description="Frame-für-Frame nach hinten — perfekt fürs Feintiming." shortcut="←">
            <button
              className="t-btn"
              onClick={() => stepFrame(-1)}
              aria-label="1 Frame zurück"
              data-tip-host="true"
            >
              ◀▏
            </button>
          </Tooltip>
          <Tooltip
            title={isPlaying ? "Pause" : "Play"}
            description={
              isPlaying
                ? "Stoppt die Wiedergabe an der aktuellen Position."
                : "Startet die Wiedergabe ab dem Playhead. Bei Loop wird am Ende von vorne begonnen."
            }
            shortcut="Space"
          >
            <button
              className="t-btn primary play"
              onClick={togglePlay}
              aria-label={isPlaying ? "Pause" : "Abspielen"}
              data-tip-host="true"
            >
              {isPlaying ? "⏸" : "▶"}
            </button>
          </Tooltip>
          <Tooltip title="Ein Frame vor" description="Frame-für-Frame nach vorn." shortcut="→">
            <button
              className="t-btn"
              onClick={() => stepFrame(1)}
              aria-label="1 Frame vor"
              data-tip-host="true"
            >
              ▕▶
            </button>
          </Tooltip>
          <Tooltip title="10 Frames vor" description="Schneller Sprung nach vorne." shortcut="⇧ →">
            <button
              className="t-btn"
              onClick={() => stepFrame(10)}
              aria-label="10 Frames vor"
              data-tip-host="true"
            >
              ⏩
            </button>
          </Tooltip>
          <Tooltip title="Ans Ende springen" description="Setzt den Playhead auf den letzten Frame." shortcut="End">
            <button className="t-btn" onClick={jumpEnd} aria-label="Zum Ende" data-tip-host="true">
              ⏭
            </button>
          </Tooltip>
        </div>

        <Tooltip
          title="Zeit / Frame-Anzeige"
          description="Aktuelle Position : Gesamtdauer in m:s.cc · darunter der absolute Frame und die Framerate des Reels."
        >
          <div className="transport-readout" aria-live="off" data-tip-host="true">
            <span className="readout-time">{fmtTime(curSec)}</span>
            <span className="readout-sep">/</span>
            <span className="readout-total">{fmtTime(totalSec)}</span>
            <span className="readout-frame">
              f {currentFrame.toString().padStart(4, "0")} · {fps}fps
            </span>
          </div>
        </Tooltip>

        <div className="transport-cluster right">
          <Tooltip
            title={
              Math.abs(speed - 1) < 0.001
                ? "Vorschau-Geschwindigkeit (Transport)"
                : `Vorschau ${speed}× — Export immer 1×`
            }
            description={
              Math.abs(speed - 1) < 0.001
                ? "Bei 1× entspricht die Vorschau dem MP4. Schneller/langsamer (Menü) ändert nur die Player-Wiedergabe, nicht die Export-Timeline."
                : "Der Player kann schneller oder langsamer laufen — das fertige Video entspricht immer der Timeline bei Normalgeschwindigkeit (so wie Vorschau mit 1×)."
            }
          >
            <Popover
              placement="top-end"
              width={160}
              trigger={({ open, toggle, ref }) => (
                <button
                  ref={ref}
                  className={`t-btn t-speed-trigger ${open ? "is-open" : ""}${
                    Math.abs(speed - 1) > 0.001 ? " speed-not-default" : ""
                  }`}
                  onClick={toggle}
                  title="Wiedergabe-Geschwindigkeit (1/2/3 für 0.5×/1×/2×)"
                  aria-label={`Geschwindigkeit ${speed}×`}
                >
                  <span className="t-speed-value">{speed}×</span>
                  <span className="t-speed-caret" aria-hidden>
                    ▾
                  </span>
                </button>
              )}
            >
            {({ close }) => (
              <MenuSection title="Geschwindigkeit">
                {SPEEDS.map((s) => (
                  <MenuItem
                    key={s}
                    icon={Math.abs(s - speed) < 0.001 ? "✓" : " "}
                    label={`${s}×`}
                    hint={s === 0.5 ? "(1)" : s === 1 ? "(2)" : s === 2 ? "(3)" : undefined}
                    active={Math.abs(s - speed) < 0.001}
                    onClick={() => { onSpeedChange(s); close(); }}
                  />
                ))}
              </MenuSection>
            )}
          </Popover>
          </Tooltip>
          <Tooltip
            title={loop ? "Loop AN" : "Loop AUS"}
            description="Spielt das Reel am Ende automatisch von vorne. Praktisch, um Übergänge im Loop zu prüfen."
            shortcut="O"
          >
            <button
              className={`t-btn toggle ${loop ? "on" : ""}`}
              onClick={onLoopToggle}
              aria-pressed={loop}
              data-tip-host="true"
            >
              🔁
            </button>
          </Tooltip>
          <Tooltip
            title={snap ? "Raster AN" : "Raster AUS"}
            description="Bei aktivem Raster snappen Clips beim Verschieben/Resizen auf 0,1s-Schritte und an Nachbar-Clipkanten + Playhead. Fürs Feintuning kurz ausschalten."
            shortcut="G"
          >
            <button
              className={`t-btn toggle ${snap ? "on" : ""}`}
              onClick={onSnapToggle}
              aria-pressed={snap}
              data-tip-host="true"
            >
              ⌗
            </button>
          </Tooltip>
          <Tooltip
            title={muted ? "Stumm" : "Ton an"}
            description="Schaltet die Vorschau-Audio-Wiedergabe stumm. Beeinflusst nur die Vorschau, NICHT den Render."
            shortcut="M"
          >
            <button
              className={`t-btn toggle ${muted ? "on" : ""}`}
              onClick={onMuteToggle}
              aria-pressed={muted}
              data-tip-host="true"
            >
              {muted ? "🔇" : "🔊"}
            </button>
          </Tooltip>
          <Tooltip
            title={isFullscreen ? "Fullscreen verlassen" : "Vollbild"}
            description={
              isFullscreen
                ? "Verlässt den Vollbildmodus. Esc geht auch."
                : "Spielt die Vorschau im Vollbild ab — perfekt für Review & Demo."
            }
            shortcut="F"
          >
            <button
              className={`t-btn toggle ${isFullscreen ? "on" : ""}`}
              onClick={toggleFullscreen}
              aria-pressed={isFullscreen}
              aria-label={isFullscreen ? "Fullscreen verlassen" : "Vollbild"}
              disabled={!player}
              data-tip-host="true"
            >
              {isFullscreen ? "🗗" : "⛶"}
            </button>
          </Tooltip>
          <Tooltip
            title={locked ? "Edit gesperrt" : "Edit frei"}
            description="Sperrt Drag/Resize von Clips & Direktmanipulation im Bild. Hilft, versehentliche Verschiebungen während Reviews zu vermeiden."
            shortcut="L"
          >
            <button
              className={`t-btn toggle lock ${locked ? "on" : ""}`}
              onClick={onLockToggle}
              aria-pressed={locked}
              data-tip-host="true"
            >
              {locked ? "🔒" : "🔓"}
            </button>
          </Tooltip>
        </div>
      </div>

      <div className="transport-row scrub">
        <Tooltip
          title="Playhead-Scrubber"
          description="Klicken & ziehen, um beliebig durchs Reel zu scrubben. Pause wird beim Ziehen automatisch ausgelöst."
        >
          <input
            type="range"
            className="transport-scrubber"
            min={0}
            max={Math.max(1, totalFrames - 1)}
            value={currentFrame}
            step={1}
            onChange={onScrub}
            aria-label="Playhead"
            data-tip-host="true"
            style={{
              background: `linear-gradient(to right, var(--accent) 0%, var(--accent) ${progressPct}%, rgba(255,255,255,0.08) ${progressPct}%, rgba(255,255,255,0.08) 100%)`,
            }}
          />
        </Tooltip>
      </div>
    </div>
  );
};

function fmtTime(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) sec = 0;
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  const ms = Math.floor((sec - Math.floor(sec)) * 100);
  return `${m}:${s.toString().padStart(2, "0")}.${ms.toString().padStart(2, "0")}`;
}
